import * as React from 'react';

import { commasAnd } from 'utils/tsUtils';
import Glyph from 'components/Glyph';
import Icon from 'components/IconDeprecated';
import Heading from 'components/Heading';
import LinkBase from 'components/LinkBase';
import MediaObject from 'components/MediaObject';
import Text from 'components/Text';
import Row from 'components/Row';

import SocialMediaDeliveryModalSectionRow from './SocialMediaDeliveryModalSectionRow';
import './DeliveryModalSection.scss';
import { SocialMediaCrossPostingDetails_api_postSummariesByMessage_socialMediaPosts as RowData } from './queries/__generated__/SocialMediaCrossPostingDetails';

export type Props = {
  isFolded: boolean;
  postDeliverySummaries: RowData[];
  toggleIsFolded: () => void;
};

function SocialMediaDeliveryModalSection({
  isFolded,
  postDeliverySummaries,
  toggleIsFolded,
}: Props) {
  const postCount = postDeliverySummaries.length;
  const socialMedias = [...new Set(postDeliverySummaries.map((post) => post.platform))];
  const hasPending = Boolean(postDeliverySummaries.find((post) => post.postStatus == 'pending'));

  const successPostCount = postDeliverySummaries.filter((post) =>
    ['delivered', 'pending'].includes(post.postStatus)
  ).length;
  const errorCount = postCount - successPostCount;

  const displayData = hasPending
    ? postDeliverySummaries.map((post) => ({
        ...post,
        // this is a serious corner case, but in case there is a discrepancy between pending/not pending statuses
        // if any post is still pending, treat them all as pending
        platformError: null,
        postLinkUrl: null,
        postStatus: 'pending',
      }))
    : postDeliverySummaries;

  let subtext = `This message was crossposted to ${commasAnd(socialMedias, true)}.`;
  if (hasPending) {
    subtext = `Posting is in progress. Posts should appear on ${commasAnd(
      socialMedias,
      true
    )} shortly.`;
  } else if (errorCount > 0) {
    subtext = `There ${
      errorCount > 1 ? 'were errors' : 'was an error'
    } while posting. Check the details below.`;
  }

  const renderPostCount = () => {
    if (hasPending) {
      return null;
    }
    if (errorCount) {
      return (
        <MediaObject gutter="xsmall" center>
          <Icon
            data-test-selector="error-icon"
            color="apple"
            name="explanation-circle"
            size="large"
          />
          <Text data-test-selector="post-success-ratio" size="xlarge">
            {' '}
            {successPostCount}/{postCount}
          </Text>
        </MediaObject>
      );
    }
    return (
      <div>
        <Text data-test-selector="post-count" size="xlarge">
          {postCount}
        </Text>
      </div>
    );
  };

  const bottomRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!isFolded) {
      bottomRef?.current?.scrollIntoView();
    }
  }, [isFolded]);

  return (
    <div className="DeliveryModalSection">
      <LinkBase className="DeliveryModalSection-header" onClick={() => toggleIsFolded()}>
        <div>
          <div>
            <Heading level="4">
              <MediaObject gutter="xsmall" center>
                <Text>Social media crossposts</Text>
                <Glyph
                  data-test="fold-icon"
                  solo
                  size="small"
                  color="mascot"
                  name={isFolded ? 'chevron-down' : 'chevron-up'}
                />
              </MediaObject>
            </Heading>
          </div>
          <div>
            <Text color="graphite" size="xxsmall">
              {subtext}
            </Text>
          </div>
        </div>
        <div>{renderPostCount()}</div>
      </LinkBase>
      <div className="DeliveryModalSection-fold">
        {!isFolded && !hasPending && (
          <Row size="xxxsmall">
            <Text color="graphite" size="xxsmall" wrap>
              Edit or remove posts directly on the platform, not on Remind.
            </Text>
          </Row>
        )}
        {!isFolded && postDeliverySummaries && (
          <div className="DeliveryModalSection-social-media">
            {displayData.map((post, i) => (
              <SocialMediaDeliveryModalSectionRow key={i} post={post} rowNumber={i} />
            ))}
          </div>
        )}
      </div>
      <div ref={bottomRef} />
    </div>
  );
}

export default SocialMediaDeliveryModalSection;
